import React from 'react';
import Section from '../components/common/Section';
import Container from '../components/common/Container';

const Privacy: React.FC = () => {
  return (
    <Section padding="lg">
      <Container size="md">
        <h1 className="text-4xl md:text-5xl font-black text-[var(--color-eel)] mb-8 text-center">
          Privacy Policy
        </h1>

        <div className="prose prose-invert max-w-none space-y-8 text-[var(--color-wolf)]">
          <div>
            <p className="text-sm text-[var(--color-hare)] mb-8">
              Last Updated: November 2025
            </p>
          </div>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">1. Introduction</h2>
            <p className="leading-relaxed mb-4">
              Lumen8 Pty Ltd ("Lumen8", "we", "our", or "us") is committed to protecting the privacy of our users,
              especially students. This Privacy Policy explains how we collect, use, disclose, and safeguard your
              information when you use our educational platform.
            </p>
            <p className="leading-relaxed">
              We comply with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth) and other
              applicable privacy laws including those specific to the protection of children's data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">2.1 Student Information</h3>
            <ul className="list-disc list-inside mb-4 space-y-2">
              <li>Name and student ID</li>
              <li>Year level and class information</li>
              <li>Learning progress and assessment data</li>
              <li>Interaction data with the platform</li>
              <li>Username and encrypted password</li>
            </ul>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">2.2 Teacher Information</h3>
            <ul className="list-disc list-inside mb-4 space-y-2">
              <li>Name and professional contact information</li>
              <li>School affiliation</li>
              <li>Classes and subjects taught</li>
              <li>Account credentials</li>
            </ul>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">2.3 Parent Information</h3>
            <ul className="list-disc list-inside mb-4 space-y-2">
              <li>Name and contact information</li>
              <li>Relationship to student(s)</li>
              <li>Account credentials</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">3. How We Use Information</h2>
            <p className="leading-relaxed mb-4">We use collected information to:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Provide adaptive learning experiences tailored to each student</li>
              <li>Track and report on student progress and mastery</li>
              <li>Generate insights for teachers and parents</li>
              <li>Improve our educational algorithms and content</li>
              <li>Communicate with users about their accounts</li>
              <li>Ensure platform security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">4. Data Sharing and Disclosure</h2>
            <p className="leading-relaxed mb-4">
              We do not sell, rent, or trade student personal information. We may share information only:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>With teachers and parents for educational purposes</li>
              <li>With schools as part of our service agreement</li>
              <li>With service providers who assist in platform operations (under strict confidentiality)</li>
              <li>When required by law or to protect safety</li>
              <li>With consent from parents/guardians for students under 18</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">5. Data Security</h2>
            <p className="leading-relaxed mb-4">
              We implement industry-standard security measures including:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>End-to-end encryption for data transmission</li>
              <li>Secure data storage with encryption at rest</li>
              <li>Regular security audits and penetration testing</li>
              <li>Access controls and authentication protocols</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">6. Data Retention</h2>
            <p className="leading-relaxed">
              We retain personal information only as long as necessary to provide our services and comply with legal
              obligations. Student progress data is retained throughout their enrollment and for a period afterward
              as required by educational regulations. Users may request deletion of their data subject to legal requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">7. Children's Privacy</h2>
            <p className="leading-relaxed mb-4">
              We are committed to protecting the privacy of children under 18. We:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Obtain verifiable consent from parents/schools before collecting student data</li>
              <li>Limit data collection to what is necessary for educational purposes</li>
              <li>Do not use student data for behavioral advertising</li>
              <li>Provide parents with access to their child's information</li>
              <li>Allow parents to request deletion of their child's data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">8. Your Rights</h2>
            <p className="leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of certain data uses</li>
              <li>Data portability where applicable</li>
              <li>Lodge a complaint with privacy authorities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">9. Cookies and Tracking</h2>
            <p className="leading-relaxed">
              We use essential cookies to maintain sessions and ensure platform functionality. We do not use
              tracking cookies for advertising purposes. Analytics cookies are used only to improve our educational
              services and are anonymized.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">10. International Data Transfers</h2>
            <p className="leading-relaxed">
              Data is primarily stored in Australia. If data is transferred internationally, we ensure appropriate
              safeguards are in place to protect your information in accordance with Australian privacy laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">11. Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy periodically. We will notify users of material changes via email
              or platform notification. Continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">12. Contact Us</h2>
            <p className="leading-relaxed mb-4">
              For questions about this Privacy Policy or our data practices, please contact:
            </p>
            <div className="bg-[var(--color-swan)] p-6 rounded-lg">
              <p className="font-semibold text-[var(--color-eel)]">Privacy Officer</p>
              <p>Lumen8 Pty Ltd</p>
              <p>Email: privacy@lumen8.it</p>
              <p>Website: www.lumen8.it</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">13. Australian Privacy Principles</h2>
            <p className="leading-relaxed">
              This policy addresses all Australian Privacy Principles including: open and transparent management,
              anonymity options where practical, collection of solicited information, dealing with unsolicited
              information, notification of collection, use and disclosure, data quality, data security, access
              and correction, and accountability for cross-border disclosure.
            </p>
          </section>
        </div>
      </Container>
    </Section>
  );
};

export default Privacy;